import { useMemo } from 'react';
import { useActiveCapabilities } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import isStringsArray from '@rsos/utils/isStringsArray';

export interface UseHasCapabilityResult {
  hasCapability: boolean | undefined;
  error: Error | null;
}

/**
 * Custom hook that checks if the current PSAP has the capability or capabilities enabled.
 */
function useHasCapability(
  capabilities?: string | string[],
): UseHasCapabilityResult {
  let error: Error | null = null;

  if (!capabilities) {
    error = Error('A capability name is required.');
  } else if (
    (typeof capabilities !== 'string' && !Array.isArray(capabilities)) ||
    (Array.isArray(capabilities) && !isStringsArray(capabilities))
  ) {
    error = Error('Capability name must be a string or an array of strings.');
  }

  const activeCapabilities: Record<string, unknown> | undefined =
    useActiveCapabilities();

  const activeCapabilitiesKeys = Object.keys(activeCapabilities || {});

  const hasCapability = useMemo<boolean | undefined>(() => {
    if (typeof capabilities === 'string') {
      return Boolean(activeCapabilities?.[capabilities]);
    } else if (Array.isArray(capabilities) && isStringsArray(capabilities)) {
      return capabilities.every(capability =>
        activeCapabilitiesKeys.includes(capability),
      );
    }
    return undefined;
  }, [activeCapabilities, activeCapabilitiesKeys, capabilities]);

  return { hasCapability, error };
}

export default useHasCapability;
